import { useEffect, useState } from 'react';
import api from '../../services/api';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatInt, FormatMoney } from '../../utils/format';
import ErrorCard from '../ui/ErrorCard';
import Select from '../ui/Select';
import StandardTable from '../ui/StandardTable';
import { Pill } from '../campaigns/campaignCardShared';
import {
    CampaignTypeLabel,
    CampaignTypePill,
    DASH,
    IntelLoading,
    IntelStatCard,
    ItemCell,
    MlbLink,
} from './intelligenceShared';

/**
 * RELATÓRIOS da Inteligência de Mercado (Fase 4) — a reconstrução dos
 * relatórios do Legacy sobre dados PRÓPRIOS (MySQL), paginados no servidor.
 *
 * Seis leituras: Resumo, Sem Campanha, Perdendo Catálogo, Dividindo Catálogo,
 * Mudanças de Campanha (com filtro de horas) e Rebates (consolidado D3 do
 * endpoint já existente /campaigns/rebates-summary).
 *
 * O relatório ativo é estado do PAI (`report`/`onReportChange`): os cards do
 * Briefing navegam direto para um relatório específico, então a escolha não
 * pode viver presa aqui dentro.
 */

const REPORTS = [
    { id: 'summary', label: 'Resumo' },
    { id: 'without-campaign', label: 'Sem Campanha' },
    { id: 'losing-catalog', label: 'Perdendo Catálogo' },
    { id: 'sharing-catalog', label: 'Dividindo Catálogo' },
    { id: 'campaign-changes', label: 'Mudanças' },
    { id: 'rebates', label: 'Rebates' },
];

/** Janela default das mudanças = 24h (mesmo recorte do relatório Legacy). */
const HOURS_OPTIONS = [
    { value: 6, label: 'Últimas 6h' },
    { value: 12, label: 'Últimas 12h' },
    { value: 24, label: 'Últimas 24h' },
    { value: 48, label: 'Últimas 48h' },
    { value: 72, label: 'Últimos 3 dias' },
    { value: 168, label: 'Últimos 7 dias' },
    { value: 720, label: 'Últimos 30 dias' },
];

const IntelligenceReports = ({ report = 'summary', onReportChange }) => {
    const activeReport = REPORTS.some((item) => item.id === report) ? report : 'summary';
    // Filtro de horas do relatório de mudanças. Vive aqui (não no filho) para
    // sobreviver à troca de relatório e voltar como o usuário deixou.
    const [hours, setHours] = useState(24);

    return (
        <div className="space-y-4">
            {/* Seletor de relatório — mesmas pills do nível 2 da página. */}
            <nav className="flex flex-wrap items-center gap-2">
                {REPORTS.map((item) => {
                    const isActive = item.id === activeReport;
                    return (
                        <button
                            type="button"
                            key={item.id}
                            onClick={() => onReportChange?.(item.id)}
                            aria-current={isActive ? 'true' : undefined}
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                                isActive
                                    ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
                                    : 'border-mono-200 bg-white text-mono-600 hover:border-mono-300 hover:text-mono-900 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400 dark:hover:border-mono-700 dark:hover:text-mono-200'
                            }`}
                        >
                            {item.label}
                        </button>
                    );
                })}
            </nav>

            {activeReport === 'summary' && <SummaryReport />}
            {activeReport === 'without-campaign' && (
                <PagedReportTable
                    // key: trocar de relatório zera página/estado — cada
                    // relatório é uma consulta independente.
                    key="without-campaign"
                    fetchPage={(params) => api.getMarketIntelligenceWithoutCampaign(params)}
                    rowsKey="items"
                    columns={WITHOUT_CAMPAIGN_COLUMNS}
                    rowKey={(row) => row.item_id}
                    countLabel="anúncios"
                    exportFileName="sem-campanha"
                    emptyMessage="Nenhum anúncio ativo sem campanha — cobertura total."
                />
            )}
            {activeReport === 'losing-catalog' && (
                <PagedReportTable
                    key="losing-catalog"
                    fetchPage={(params) => api.getMarketIntelligenceLosingCatalog(params)}
                    rowsKey="items"
                    columns={CATALOG_COLUMNS}
                    rowKey={(row) => row.item_id}
                    countLabel="anúncios"
                    exportFileName="perdendo-catalogo"
                    emptyMessage="Nenhum anúncio perdendo o catálogo."
                />
            )}
            {activeReport === 'sharing-catalog' && (
                <PagedReportTable
                    key="sharing-catalog"
                    fetchPage={(params) => api.getMarketIntelligenceSharingCatalog(params)}
                    rowsKey="items"
                    columns={CATALOG_COLUMNS}
                    rowKey={(row) => row.item_id}
                    countLabel="anúncios"
                    exportFileName="dividindo-catalogo"
                    emptyMessage="Nenhum anúncio dividindo o 1º lugar do catálogo."
                />
            )}
            {activeReport === 'campaign-changes' && (
                <PagedReportTable
                    // key com as horas: mudar a janela é outra consulta —
                    // recomeçar da página 1 é o comportamento certo.
                    key={`campaign-changes-${hours}`}
                    fetchPage={(params) => api.getMarketIntelligenceCampaignChanges({ ...params, hours })}
                    rowsKey="events"
                    columns={CHANGES_COLUMNS}
                    rowKey={(row) => `${row.item_id}:${row.campaign_id}:${row.event_type}:${row.created_at}`}
                    countLabel="mudanças"
                    exportFileName="mudancas-campanha"
                    emptyMessage="Nenhuma mudança de campanha na janela selecionada."
                    toolbarLeft={
                        <div className="w-44">
                            <Select value={hours} onChange={(event) => setHours(Number(event.target.value))} className="px-2 py-1 text-sm">
                                {HOURS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    }
                />
            )}
            {activeReport === 'rebates' && <RebatesReport />}
        </div>
    );
};

/* ── Resumo ──────────────────────────────────────────────────────────────── */

const SummaryReport = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = await api.getMarketIntelligenceReportsSummary();
                if (!cancelled) {
                    setData(payload || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar o resumo.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [refreshTick]);

    if (error) {
        return (
            <ErrorCard
                title="Erro ao carregar o resumo"
                message={error}
                action="Tentar novamente"
                actionFn={() => setRefreshTick((tick) => tick + 1)}
            />
        );
    }
    if (loading) {
        return <IntelLoading text="Carregando resumo…" />;
    }

    return (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <IntelStatCard label="Anúncios ativos" value={FormatInt(data?.active_items || 0)} />
            <IntelStatCard label="Com campanha" value={FormatInt(data?.items_with_campaign || 0)} tone="success" />
            <IntelStatCard label="Sem campanha" value={FormatInt(data?.items_without_campaign || 0)} />
            <IntelStatCard label="Perdendo catálogo" value={FormatInt(data?.losing_catalog || 0)} tone="danger" />
            <IntelStatCard label="Dividindo catálogo" value={FormatInt(data?.sharing_catalog || 0)} tone="warning" />
            <IntelStatCard label="Atenção" value={FormatInt(data?.attention_items || 0)} tone="danger" />
        </div>
    );
};

/* ── Tabelas paginadas no servidor ───────────────────────────────────────── */

// sortable:false em TODAS as colunas paginadas no servidor: os endpoints da
// Inteligência não aceitam parâmetro de sort — a ordem é a da consulta do
// servidor, e um cabeçalho clicável que não reordena seria UX enganosa
// (mesmo padrão de WarehouseLpnsPage).
const WITHOUT_CAMPAIGN_COLUMNS = [
    {
        key: 'item',
        label: 'Anúncio',
        sortable: false,
        getValue: (row) => `${row.title || ''} ${row.sku || ''}`,
        render: (row) => <ItemCell title={row.title} sku={row.sku} />,
    },
    {
        key: 'item_id',
        label: 'MLB',
        sortable: false,
        getValue: (row) => row.item_id,
        render: (row) => <MlbLink itemId={row.item_id} />,
    },
    {
        key: 'price',
        label: 'Preço',
        align: 'right',
        sortable: false,
        getValue: (row) => FormatMoney(row.price),
        render: (row) => <span className="font-semibold tabular-nums">{FormatMoney(row.price)}</span>,
    },
    {
        key: 'sold_7d',
        label: 'Vendas 7d',
        align: 'right',
        sortable: false,
        getValue: (row) => Number(row.sold_7d || 0),
        render: (row) => <span className="tabular-nums">{FormatInt(row.sold_7d || 0)}</span>,
    },
];

/**
 * Perdendo e Dividindo têm o MESMO shape no wire — uma definição só.
 * sortable:false em todas: ordem é a da consulta do servidor (o endpoint não
 * aceita sort), como em WITHOUT_CAMPAIGN_COLUMNS.
 */
const CATALOG_COLUMNS = [
    {
        key: 'item',
        label: 'Anúncio',
        sortable: false,
        getValue: (row) => `${row.title || ''} ${row.sku || ''}`,
        render: (row) => <ItemCell title={row.title} sku={row.sku} />,
    },
    {
        key: 'item_id',
        label: 'MLB',
        sortable: false,
        getValue: (row) => row.item_id,
        render: (row) => <MlbLink itemId={row.item_id} />,
    },
    {
        key: 'price',
        label: 'Meu preço',
        align: 'right',
        sortable: false,
        getValue: (row) => FormatMoney(row.price),
        render: (row) => <span className="font-semibold tabular-nums">{FormatMoney(row.price)}</span>,
    },
    {
        key: 'price_to_win',
        label: 'Preço p/ ganhar',
        align: 'right',
        sortable: false,
        getValue: (row) => FormatMoney(row.price_to_win),
        render: (row) => (
            <span className="font-semibold tabular-nums text-success-600 dark:text-success-400">
                {FormatMoney(row.price_to_win)}
            </span>
        ),
    },
    {
        key: 'winner_price',
        label: 'Preço do ganhador',
        align: 'right',
        sortable: false,
        getValue: (row) => FormatMoney(row.winner_price),
    },
    {
        key: 'winner_nickname',
        label: 'Ganhador',
        sortable: false,
        getValue: (row) => row.winner_nickname || DASH,
        render: (row) => <span className="text-xs">{row.winner_nickname || DASH}</span>,
    },
    {
        key: 'competitors_count',
        label: 'Concorrentes',
        align: 'center',
        sortable: false,
        getValue: (row) => Number(row.competitors_count || 0),
        render: (row) => (
            <span className="inline-flex rounded-full bg-warning-100 px-2 py-0.5 text-[11px] font-semibold text-warning-800 dark:bg-warning-900/40 dark:text-warning-300">
                {FormatInt(row.competitors_count || 0)}
            </span>
        ),
    },
];

/** event_type do histórico → rótulo + tom (desconhecido sai cru em neutro). */
const EVENT_BADGES = {
    ENTERED: { label: 'Entrou', tone: 'success' },
    CHANGED: { label: 'Mudou', tone: 'warning' },
    LEFT: { label: 'Saiu', tone: 'danger' },
    campaign_activated: { label: 'Ativada', tone: 'success' },
    campaign_changed: { label: 'Alterada', tone: 'warning' },
    campaign_deactivated: { label: 'Desativada', tone: 'danger' },
};

// sortable:false em todas: ordem é a da consulta do servidor (o endpoint de
// mudanças não aceita sort), como em WITHOUT_CAMPAIGN_COLUMNS.
const CHANGES_COLUMNS = [
    {
        key: 'event_type',
        label: 'Evento',
        sortable: false,
        getValue: (row) => EVENT_BADGES[row.event_type]?.label || row.event_type || DASH,
        render: (row) => {
            const badge = EVENT_BADGES[row.event_type];
            return <Pill tone={badge?.tone || 'mono'}>{badge?.label || row.event_type || DASH}</Pill>;
        },
    },
    {
        key: 'item',
        label: 'Anúncio',
        sortable: false,
        getValue: (row) => row.title || row.item_id || '',
        render: (row) => <ItemCell title={row.title} sku={null} />,
    },
    {
        key: 'item_id',
        label: 'MLB',
        sortable: false,
        getValue: (row) => row.item_id,
        render: (row) => <MlbLink itemId={row.item_id} />,
    },
    {
        key: 'campaign',
        label: 'Campanha',
        sortable: false,
        getValue: (row) => row.campaign_name || row.campaign_id || '',
        render: (row) => <span className="text-sm text-black dark:text-white">{row.campaign_name || row.campaign_id || DASH}</span>,
    },
    {
        key: 'status',
        label: 'Status',
        sortable: false,
        getValue: (row) => `${row.old_status || ''} ${row.new_status || ''}`,
        render: (row) => (
            <span className="whitespace-nowrap text-xs tabular-nums">
                <span className="text-mono-500">{row.old_status || DASH}</span>
                <span className="mx-1 text-mono-400">→</span>
                <span className="font-semibold text-black dark:text-white">{row.new_status || DASH}</span>
            </span>
        ),
    },
    {
        key: 'created_at',
        label: 'Quando',
        sortable: false,
        getValue: (row) => row.created_at || '',
        render: (row) => <span className="whitespace-nowrap text-xs">{FormatDateTimeBr(row.created_at)}</span>,
    },
];

/**
 * Tabela paginada NO SERVIDOR (o wire manda `total` + aceita limit/offset).
 * Busca client-side fica DESLIGADA de propósito: filtrar só a página carregada
 * mentiria o resultado — quem quiser recorte usa os filtros do relatório.
 * Sort também é do servidor (ordem que a consulta definiu).
 */
const PagedReportTable = ({
    fetchPage,
    rowsKey,
    columns,
    rowKey,
    countLabel,
    exportFileName,
    emptyMessage,
    toolbarLeft = null,
}) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = await fetchPage({ limit: pageSize, offset: (page - 1) * pageSize });
                if (!cancelled) {
                    const list = Array.isArray(payload?.[rowsKey]) ? payload[rowsKey] : [];
                    setRows(list);
                    setTotal(Number(payload?.total ?? list.length));
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar o relatório.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
        // fetchPage chega como arrow inline do pai; o `key` do componente já
        // remonta a tabela quando a CONSULTA muda (relatório/horas), então
        // depender da identidade da função só causaria refetch por re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, refreshTick, rowsKey]);

    if (error) {
        return (
            <ErrorCard
                title="Erro ao carregar o relatório"
                message={error}
                action="Tentar novamente"
                actionFn={() => setRefreshTick((tick) => tick + 1)}
            />
        );
    }

    // Primeira carga (sem linha nenhuma ainda): não renderizar a tabela — o
    // emptyMessage definitivo ("cobertura total" etc.) não pode aparecer antes
    // de a resposta existir. Refetch de paginação mantém a página anterior
    // visível sob o indicador (mesmo padrão isInitialLoading de Orders.jsx).
    if (loading && rows.length === 0) {
        return <IntelLoading />;
    }

    return (
        <div className="space-y-2">
            {loading ? <IntelLoading /> : null}
            <StandardTable
                rows={rows}
                columns={columns}
                rowKey={rowKey}
                countLabel={countLabel}
                emptyMessage={emptyMessage}
                exportFileName={exportFileName}
                searchEnabled={false}
                sortMode="server"
                paginationMode="server"
                totalItems={total}
                currentPage={page}
                onPageChange={setPage}
                itemsPerPage={pageSize}
                onItemsPerPageChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
                toolbarLeft={toolbarLeft}
            />
        </div>
    );
};

/* ── Rebates (D3 — endpoint já existente de /campaigns) ──────────────────── */

// getValue numérico em todas: as tabelas do Rebates ordenam no CLIENTE — a
// string formatada ('R$ …') ordenaria lexicograficamente; FormatMoney é do render.
const REBATE_GROUP_COLUMNS = [
    {
        key: 'items',
        label: 'Itens',
        align: 'right',
        getValue: (row) => Number(row.items || 0),
        render: (row) => <span className="tabular-nums">{FormatInt(row.items || 0)}</span>,
    },
    {
        key: 'rebate_ml',
        label: 'Rebate ML',
        align: 'right',
        getValue: (row) => Number(row.rebate_ml || 0),
        render: (row) => (
            <span className="font-semibold tabular-nums text-success-600 dark:text-success-400">
                {FormatMoney(row.rebate_ml)}
            </span>
        ),
    },
    {
        key: 'rebate_seller',
        label: 'Rebate vendedor',
        align: 'right',
        getValue: (row) => Number(row.rebate_seller || 0),
        render: (row) => <span className="font-semibold tabular-nums">{FormatMoney(row.rebate_seller)}</span>,
    },
];

const RebatesReport = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = await api.getCampaignRebatesSummary();
                if (!cancelled) {
                    setData(payload || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar o consolidado de rebates.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [refreshTick]);

    if (error) {
        return (
            <ErrorCard
                title="Erro ao carregar rebates"
                message={error}
                action="Tentar novamente"
                actionFn={() => setRefreshTick((tick) => tick + 1)}
            />
        );
    }
    if (loading) {
        return <IntelLoading text="Carregando rebates…" />;
    }

    const totals = data?.totals || {};
    const byType = Array.isArray(data?.by_type) ? data.by_type : [];
    const byCampaign = Array.isArray(data?.by_campaign) ? data.by_campaign : [];

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <IntelStatCard
                    label="Rebate ML (subsídio)"
                    value={FormatMoney(totals.rebate_ml)}
                    sub="Quanto o ML banca nas participações vigentes"
                    tone="success"
                />
                <IntelStatCard
                    label="Rebate vendedor"
                    value={FormatMoney(totals.rebate_seller)}
                    sub="Quanto sai do nosso preço"
                />
                <IntelStatCard label="Participações" value={FormatInt(totals.items || 0)} sub="Itens em campanha vigente" />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div>
                    <p className="mb-2 text-sm font-semibold text-black dark:text-white">Por tipo de campanha</p>
                    <StandardTable
                        variant="minimal"
                        rows={byType}
                        rowKey={(row) => row.type || 'sem-tipo'}
                        emptyMessage="Nenhuma participação vigente."
                        columns={[
                            {
                                key: 'type',
                                label: 'Tipo',
                                getValue: (row) => CampaignTypeLabel(row.type),
                                render: (row) => <CampaignTypePill type={row.type} />,
                            },
                            ...REBATE_GROUP_COLUMNS,
                        ]}
                    />
                </div>
                <div>
                    <p className="mb-2 text-sm font-semibold text-black dark:text-white">Por campanha</p>
                    <StandardTable
                        rows={byCampaign}
                        rowKey={(row) => row.campaign_id}
                        countLabel="campanhas"
                        emptyMessage="Nenhuma participação vigente."
                        exportFileName="rebates-por-campanha"
                        columns={[
                            {
                                key: 'campaign',
                                label: 'Campanha',
                                getValue: (row) => row.campaign_name || row.campaign_id || '',
                                render: (row) => (
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-black dark:text-white">
                                            {row.campaign_name || row.campaign_id}
                                        </p>
                                        <p className="mt-0.5 text-xs text-mono-500">{CampaignTypeLabel(row.type)}</p>
                                    </div>
                                ),
                            },
                            ...REBATE_GROUP_COLUMNS,
                        ]}
                    />
                </div>
            </div>
        </div>
    );
};

export default IntelligenceReports;
